/**
 * Collection State Manager
 * 
 * Manages collection page state using sessionStorage for instant restoration
 * when users navigate back from product pages.
 */
class CollectionStateManager {
  constructor(options = {}) {
    this.config = {
      storageKey: 'piano_collection_state',
      expectedDestinationKey: 'piano_expected_destination',
      navigationIdKey: 'piano_navigation_id',
      maxAge: 5 * 60 * 1000, // 5 minutes in milliseconds
      maxSize: 4 * 1024 * 1024, // 4MB safe limit for sessionStorage
      debug: options.debug || false,
      ...options
    };
    
    // Initialize development logging
    this.isDevelopment = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
    this.logKey = 'piano_collection_debug_logs';
    
    console.log('[CollectionStateManager] Constructor called', this.config);
    this.log('CollectionStateManager initialized', this.config);
    this.devLog('CollectionStateManager constructor', {
      config: this.config,
      isDevelopment: this.isDevelopment,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Generate unique navigation ID for tracking
   */
  generateNavigationId() {
    return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Clean HTML content by removing unnecessary whitespace while preserving structure
   */
  cleanHTML(html) {
    if (!html) return '';
    
    return html
      .replace(/>\s+</g, '><') // Remove whitespace between tags
      .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space
      .trim();
  }

  /**
   * Compress HTML content if it exceeds size limits
   */
  compressHTML(html) {
    if (!html) return '';
    
    // Remove HTML comments
    let compressed = html.replace(/<!--[\s\S]*?-->/g, '');
    
    // Remove extra whitespace more aggressively
    compressed = compressed
      .replace(/\s+/g, ' ') // Multiple whitespace to single space
      .replace(/>\s+</g, '><') // Remove whitespace between tags
      .trim();
    
    return compressed;
  }

  /**
   * Save the current collection state
   */
  saveState(data = {}) {
    console.log('[CollectionStateManager] saveState called with data:', data);
    this.devLog('saveState called', {
      inputData: data,
      sessionStorage: {
        expectedDestination: sessionStorage.getItem(this.config.expectedDestinationKey),
        existingState: sessionStorage.getItem(this.config.storageKey) ? 'exists' : 'none'
      }
    });
    
    try {
      const productsGrid = document.getElementById('products-grid');
      if (!productsGrid) {
        console.log('[CollectionStateManager] Products grid not found!');
        this.log('Products grid not found, cannot save state');
        this.devLog('saveState failed - no products grid', {
          availableElements: Array.from(document.querySelectorAll('[id]')).map(el => el.id)
        });
        return false;
      }

      // Get current state data
      const currentUrl = window.location.href;
      const scrollPosition = window.scrollY;
      const timestamp = Date.now();
      
      // Check if we have existing state to preserve
      const existingState = this.getState();
      let shouldPreserveExisting = false;
      
      if (existingState && this.isStateValid(existingState)) {
        // Use pagination state as the primary indicator, not HTML parsing
        const existingPages = existingState.paginationState?.pagesLoaded || 1;
        const currentPages = data.pagesLoaded || 1;
        const existingTotalProducts = existingState.paginationState?.totalProducts || 0;
        const currentTotalProducts = data.totalProducts || 0;
        
        this.devLog('Comparing existing vs current state', {
          existingPages,
          currentPages,
          existingTotalProducts,
          currentTotalProducts,
          existingTimestamp: existingState.timestamp,
          ageOfExisting: Date.now() - existingState.timestamp
        });
        
        // Preserve if existing state has more pages OR more total products
        // This handles the case where user has loaded more content previously
        shouldPreserveExisting = existingPages > currentPages || 
                               (existingPages === currentPages && existingTotalProducts > currentTotalProducts);
        
        this.devLog('State preservation decision', {
          shouldPreserveExisting,
          reason: shouldPreserveExisting ? 
            (existingPages > currentPages ? 'more pages in existing' : 'more products in existing') :
            'current state is equal or better'
        });
      }
      
      let productsHTML, paginationState;
      
      if (shouldPreserveExisting) {
        // Preserve the larger state but update scroll position and clicked product
        this.devLog('Preserving existing larger state', {
          preservedProducts: existingState.productsHTML.match(/data-product-id/g)?.length,
          updatedScrollPosition: scrollPosition
        });
        
        productsHTML = existingState.productsHTML;
        paginationState = existingState.paginationState;
      } else {
        // Use current state
        productsHTML = productsGrid.innerHTML;
        productsHTML = this.cleanHTML(productsHTML);
        
        // Check size and compress if needed
        const estimatedSize = new Blob([productsHTML]).size;
        if (estimatedSize > this.config.maxSize * 0.8) {
          this.log('HTML content large, applying compression');
          productsHTML = this.compressHTML(productsHTML);
        }
        
        paginationState = {
          currentPage: data.currentPage || 1,
          pagesLoaded: data.pagesLoaded || 1,
          totalProducts: data.totalProducts || 0
        };
      }
      
      // Create state object
      const state = {
        url: currentUrl,
        baseUrl: currentUrl.split('?')[0],
        search: window.location.search,
        scrollPosition, // Always update scroll position
        timestamp,
        navigationId: this.generateNavigationId(),
        productsHTML,
        paginationState,
        filterState: {
          hasFilters: window.location.search.includes('filter'),
          searchParams: window.location.search
        },
        lastClickedProduct: data.clickedProductId || data.lastClickedProduct || (existingState && existingState.lastClickedProduct),
        preservedFromPrevious: shouldPreserveExisting,
        ...data
      };

      // Validate size before saving
      const stateSize = new Blob([JSON.stringify(state)]).size;
      if (stateSize > this.config.maxSize) {
        this.log('State too large for sessionStorage, skipping save', stateSize);
        return false;
      }

      // Save to sessionStorage
      sessionStorage.setItem(this.config.storageKey, JSON.stringify(state));
      sessionStorage.setItem(this.config.navigationIdKey, state.navigationId);
      
      this.log('State saved successfully', {
        size: stateSize,
        productsCount: state.productsHTML.match(/data-product-id/g)?.length || 0,
        pagesLoaded: state.paginationState.pagesLoaded
      });
      
      this.devLog('saveState successful', {
        state: {
          ...state,
          productsHTML: `[HTML content: ${state.productsHTML.length} chars]` // Don't log full HTML
        },
        stateSize,
        sessionStorageKeys: Object.keys(sessionStorage)
      });
      
      return true;
    } catch (error) {
      this.log('Error saving state:', error);
      return false;
    }
  }

  /**
   * Retrieve saved state from sessionStorage
   */
  getState() {
    try {
      const saved = sessionStorage.getItem(this.config.storageKey);
      if (!saved) {
        this.log('No saved state found');
        return null;
      }

      const state = JSON.parse(saved);
      this.log('Retrieved state', {
        age: Date.now() - state.timestamp,
        url: state.url,
        pagesLoaded: state.paginationState?.pagesLoaded
      });
      
      return state;
    } catch (error) {
      this.log('Error retrieving state:', error);
      this.invalidateCache();
      return null;
    }
  }

  /**
   * Validate if saved state should be used
   */
  isStateValid(state) {
    if (!state) return false;

    const currentTime = Date.now();
    const currentUrl = window.location.href;
    const currentBaseUrl = currentUrl.split('?')[0];

    // Check age (max 5 minutes)
    if (currentTime - state.timestamp > this.config.maxAge) {
      this.log('State expired', { age: currentTime - state.timestamp });
      return false;
    }

    // Check URL match (base URL should be the same)
    if (state.baseUrl !== currentBaseUrl) {
      this.log('URL mismatch', { saved: state.baseUrl, current: currentBaseUrl });
      return false;
    }

    // Check filter consistency
    const currentSearch = window.location.search;
    if (state.search !== currentSearch) {
      this.log('Filter mismatch', { saved: state.search, current: currentSearch });
      return false;
    }

    return true;
  }

  /**
   * Detect if user navigated back from tracked destination
   */
  detectBackNavigation() {
    this.devLog('detectBackNavigation called', {
      currentUrl: window.location.href,
      performance: {
        navigation: performance.navigation ? {
          type: performance.navigation.type,
          TYPE_BACK_FORWARD: performance.navigation.TYPE_BACK_FORWARD
        } : 'not available',
        navigationEntries: performance.getEntriesByType ? 
          performance.getEntriesByType('navigation').map(entry => ({type: entry.type})) : 
          'not available'
      }
    });
    
    // Method 1: Check if we have an expected destination (indicating tracked navigation)
    const expectedDestination = sessionStorage.getItem(this.config.expectedDestinationKey);
    if (!expectedDestination) {
      this.log('No tracked navigation detected');
      this.devLog('detectBackNavigation - no expected destination', {
        sessionStorageKeys: Object.keys(sessionStorage),
        expectedDestinationKey: this.config.expectedDestinationKey
      });
      return false;
    }
    
    // Method 1a: Check if current URL matches the expected destination
    // If it does, this means we're at the destination, not returning from it
    const currentUrl = window.location.href;
    if (currentUrl === expectedDestination) {
      this.log('At expected destination, not returning from it');
      this.devLog('detectBackNavigation - at destination', {
        currentUrl,
        expectedDestination
      });
      return false;
    }

    // Method 2: Performance Navigation API (modern browsers) - DEFINITIVE CHECK
    if (performance.navigation && performance.navigation.type === performance.navigation.TYPE_BACK_FORWARD) {
      this.log('Back navigation detected via Performance Navigation API');
      this.devLog('detectBackNavigation - Performance Navigation API positive', {
        navigationType: performance.navigation.type,
        expectedDestination
      });
      return true;
    }

    // Method 3: Navigation Timing API (newer approach) - DEFINITIVE CHECK
    if (performance.getEntriesByType) {
      const navigationEntries = performance.getEntriesByType('navigation');
      if (navigationEntries.length > 0 && navigationEntries[0].type === 'back_forward') {
        this.log('Back navigation detected via Navigation Timing API');
        this.devLog('detectBackNavigation - Navigation Timing API positive', {
          navigationEntry: navigationEntries[0],
          expectedDestination
        });
        return true;
      }
    }

    // Check if browser explicitly indicates this is NOT back navigation
    const isExplicitlyNotBackNavigation = 
      (performance.navigation && performance.navigation.type === performance.navigation.TYPE_NAVIGATE) ||
      (performance.getEntriesByType && 
       performance.getEntriesByType('navigation').length > 0 && 
       performance.getEntriesByType('navigation')[0].type === 'navigate');

    if (isExplicitlyNotBackNavigation) {
      this.log('Browser indicates this is a fresh navigation, not back navigation');
      this.devLog('detectBackNavigation - explicit fresh navigation detected', {
        navigationType: performance.navigation ? performance.navigation.type : 'unknown',
        navigationEntry: performance.getEntriesByType ? 
          performance.getEntriesByType('navigation')[0] : 'unknown',
        expectedDestination
      });
      return false;
    }

    // Method 4: Navigation ID tracking (fallback only if browser APIs are inconclusive)
    const savedNavigationId = sessionStorage.getItem(this.config.navigationIdKey);
    const state = this.getState();
    if (savedNavigationId && state && state.navigationId === savedNavigationId) {
      this.log('Back navigation suggested by navigation ID tracking');
      this.devLog('detectBackNavigation - Navigation ID match (fallback)', {
        savedNavigationId,
        stateNavigationId: state.navigationId,
        expectedDestination,
        note: 'Browser APIs were inconclusive'
      });
      return true;
    }

    // Final fallback: If expected destination is set but no clear indication
    this.log('Navigation type unclear, not using cache');
    this.devLog('detectBackNavigation - inconclusive, defaulting to false', {
      expectedDestination,
      reason: 'Cannot determine navigation type definitively'
    });
    return false;
  }

  /**
   * Main method to determine if cache should be used
   */
  shouldUseCache() {
    this.devLog('shouldUseCache called', {
      currentUrl: window.location.href,
      sessionStorageKeys: Object.keys(sessionStorage)
    });
    
    // Check if back navigation was detected
    const backNavDetected = this.detectBackNavigation();
    if (!backNavDetected) {
      this.devLog('shouldUseCache - back navigation not detected', {});
      return false;
    }

    // Get and validate saved state
    const state = this.getState();
    const stateValid = this.isStateValid(state);
    if (!stateValid) {
      this.invalidateCache();
      this.devLog('shouldUseCache - state invalid, cache invalidated', {
        state: state ? 'exists' : 'null',
        stateAge: state ? Date.now() - state.timestamp : 'n/a'
      });
      return false;
    }

    this.log('Cache should be used');
    this.devLog('shouldUseCache - cache will be used', {
      stateAge: Date.now() - state.timestamp,
      stateUrl: state.url,
      currentUrl: window.location.href
    });
    return true;
  }

  /**
   * Restore saved state with callbacks for component reinitialization
   */
  async restoreState(callbacks = {}) {
    try {
      const state = this.getState();
      if (!state || !this.isStateValid(state)) {
        this.log('Invalid state for restoration');
        return false;
      }

      const productsGrid = document.getElementById('products-grid');
      if (!productsGrid) {
        this.log('Products grid not found for restoration');
        return false;
      }

      this.log('Starting state restoration');

      // Restore HTML content
      productsGrid.innerHTML = state.productsHTML;

      // Execute reinitialization callbacks
      if (callbacks.beforeRestore) {
        await callbacks.beforeRestore(state);
      }

      // Component reinitialization
      if (callbacks.reinitializeComponents) {
        await callbacks.reinitializeComponents();
      } else {
        // Default component reinitialization
        if (typeof window.fadeInLazyImages === 'function') {
          window.fadeInLazyImages(productsGrid);
        }
        if (typeof window.initializeNewSwipers === 'function') {
          await window.initializeNewSwipers();
        }
        if (typeof window.attachProductLinkListeners === 'function') {
          window.attachProductLinkListeners();
        }
        if (typeof window.initializeProductItems === 'function') {
          window.initializeProductItems(productsGrid);
        }
      }

      // Restore scroll position (with delay to ensure DOM is ready)
      setTimeout(() => {
        window.scrollTo({
          top: state.scrollPosition,
          behavior: 'instant' // Use instant for better UX on restoration
        });
      }, 100);

      // Restore pagination state
      if (state.paginationState && callbacks.restorePagination) {
        callbacks.restorePagination(state);
      }

      // Execute post-restore callback
      if (callbacks.afterRestore) {
        await callbacks.afterRestore(state);
      }

      // Clean up flags and temporary data
      this.clearExpectedDestination();
      
      this.log('State restoration completed successfully');
      return true;

    } catch (error) {
      this.log('Error during state restoration:', error);
      return false;
    }
  }

  /**
   * Invalidate cached state
   */
  invalidateCache() {
    sessionStorage.removeItem(this.config.storageKey);
    sessionStorage.removeItem(this.config.navigationIdKey);
    this.clearExpectedDestination();
    this.log('Cache invalidated');
  }

  /**
   * Set expected destination (called by template when user clicks tracked links)
   * Note: This is now handled by the template code, keeping this for backwards compatibility
   */
  setExpectingBackFlag() {
    console.log('[CollectionStateManager] Setting expecting back flag (deprecated - use piano_expected_destination)');
    // This method is kept for backwards compatibility but the actual logic
    // now uses piano_expected_destination set by the template
    this.log('Expecting back flag set (deprecated)');
  }

  /**
   * Clear the expected destination
   */
  clearExpectedDestination() {
    sessionStorage.removeItem(this.config.expectedDestinationKey);
    this.log('Expected destination cleared');
  }
  
  /**
   * Clear the expecting back flag (backwards compatibility)
   */
  clearExpectingBackFlag() {
    this.clearExpectedDestination();
  }

  /**
   * Debug logging
   */
  log(message, data = null) {
    if (this.config.debug) {
      console.log(`[CollectionStateManager] ${message}`, data || '');
    }
  }

  /**
   * Development logging with session storage
   */
  devLog(action, data = null) {
    if (!this.isDevelopment) return;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      time: Date.now(),
      action,
      data,
      url: window.location.href,
      userAgent: navigator.userAgent.substring(0, 100)
    };
    
    // Console log for immediate debugging
    console.log(`[DEV-LOG] ${action}`, logEntry);
    
    // Save to sessionStorage
    try {
      const existingLogs = JSON.parse(sessionStorage.getItem(this.logKey) || '[]');
      existingLogs.push(logEntry);
      
      // Keep only last 100 entries to prevent storage overflow
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }
      
      sessionStorage.setItem(this.logKey, JSON.stringify(existingLogs));
    } catch (e) {
      console.warn('[DEV-LOG] Failed to save log to sessionStorage:', e);
    }
  }

  /**
   * Get all development logs
   */
  getDevLogs() {
    if (!this.isDevelopment) return [];
    try {
      return JSON.parse(sessionStorage.getItem(this.logKey) || '[]');
    } catch (e) {
      console.warn('[DEV-LOG] Failed to retrieve logs:', e);
      return [];
    }
  }

  /**
   * Clear development logs
   */
  clearDevLogs() {
    if (!this.isDevelopment) return;
    sessionStorage.removeItem(this.logKey);
    console.log('[DEV-LOG] Logs cleared');
  }

  /**
   * Export logs as formatted text for analysis
   */
  exportLogs() {
    if (!this.isDevelopment) return 'Development logging not available';
    
    const logs = this.getDevLogs();
    let output = `=== PIANO COLLECTION STATE MANAGER DEBUG LOGS ===\n`;
    output += `Generated: ${new Date().toISOString()}\n`;
    output += `Total entries: ${logs.length}\n\n`;
    
    logs.forEach((log, index) => {
      output += `[${index + 1}] ${log.timestamp} - ${log.action}\n`;
      output += `URL: ${log.url}\n`;
      if (log.data) {
        output += `Data: ${JSON.stringify(log.data, null, 2)}\n`;
      }
      output += `---\n`;
    });
    
    return output;
  }
}

// Export for use in collection template
if (typeof window !== 'undefined') {
  window.CollectionStateManager = CollectionStateManager;
}